import {
  type StoragePrivacy,
  type URLFetcherConfig,
  createDirectoryUrlsFetcher,
} from '@supabase-cache-helpers/storage-core';
import type { FileObject, StorageError } from '@supabase/storage-js';
import {
  type UseQueryOptions as UseReactQueryOptions,
  type UseQueryResult as UseReactQueryResult,
  useQuery as useReactQuery,
} from '@tanstack/react-query';

import { type StorageFileApi, encode } from '../lib';

function buildDirectoryUrlsQueryOpts(
  fileApi: StorageFileApi,
  path: string,
  mode: StoragePrivacy,
  config?: Omit<
    UseReactQueryOptions<
      (FileObject & { url: string })[] | undefined,
      StorageError
    >,
    'queryKey' | 'queryFn'
  > &
    Pick<URLFetcherConfig, 'expiresIn'>,
): UseReactQueryOptions<
  (FileObject & { url: string })[] | undefined,
  StorageError
> {
  return {
    queryKey: encode([fileApi, path]),
    queryFn: () => createDirectoryUrlsFetcher(mode, config)(fileApi, path),
    ...config,
  };
}

/**
 * Convenience hook to fetch all files in a directory, and their corresponding URLs, from Supabase Storage using React Query.
 *
 * @param {StorageFileApi} fileApi - The file API of the storage bucket.
 * @param {string|null} path - The path of the directory to fetch files from.
 * @param {StoragePrivacy} mode - The privacy mode of the bucket to fetch files from.
 * @param {UseQueryOptions & Pick<URLFetcherConfig, 'expiresIn'>} [config] - Optional SWR configuration and `expiresIn` value to pass to the `createDirectoryUrlsFetcher` function.
 *
 * @returns {UseQueryResult<(FileObject & { url: string })[] | undefined, StorageError>} A React Query response containing an array of file objects with their corresponding URLs.
 */
function useDirectoryFileUrls(
  fileApi: StorageFileApi,
  path: string,
  mode: StoragePrivacy,
  config?: Omit<
    UseReactQueryOptions<
      (FileObject & { url: string })[] | undefined,
      StorageError
    >,
    'queryKey' | 'queryFn'
  > &
    Pick<URLFetcherConfig, 'expiresIn'>,
): UseReactQueryResult<
  (FileObject & { url: string })[] | undefined,
  StorageError
> {
  return useReactQuery<
    (FileObject & { url: string })[] | undefined,
    StorageError
  >(buildDirectoryUrlsQueryOpts(fileApi, path, mode, config));
}

export { useDirectoryFileUrls, buildDirectoryUrlsQueryOpts };
